import React from 'react'
import PropTypes from 'prop-types'
import R from 'ramda'
import {
  notNilOrEmpty,
  isNilOrEmpty,
  getFsUrl
} from '_utils'

import {
  get,
  patch,
  post
} from '_utils/xhr'

import {
  Grid,
  Dimmer,
  Loader,
  Image,
  Divider,
  Button,
  Form,
  Segment
} from 'semantic-ui-react'

import BaseInputField from '_forms/baseInput'
import LineItem from '_views/lineItem'
import BaseSelectField from '_forms/baseSelect'
import FileItem from '_forms/fileItem'
import { SingularSearch as UserSearch } from '_forms/userSearch'
import { SingularSearch as LicenseSearch } from '_forms/licenseSearch'
import { SingularSearch as SerialSearch } from '_forms/serialSearch'

import { licenseIsUpgradable } from '_utils/licenses'

// const nullChange = _e => null
const UPLOAD_ACCEPT = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'application/pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.pdf'
].join(',')

const Max4LiveSerial = ({onChange, attributes}) => (
  <BaseInputField width={10} name='max4live_serial'
    type='text'
    value={R.prop('max4live_serial', attributes)}
    onChange={onChange}
    labelTxt='Max4Live Serial Number'
    popupTxt='Please enter a valid and unused Max4Live Serial Number.'
  />
)

Max4LiveSerial.propTypes = {
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

class Max6Serial extends React.PureComponent {
  state = {
    serial: null,
    error: null
  }

  handleChange = (_e, serial) => {
    const licId = R.path(['license', '_id'], serial)
    const error = isNilOrEmpty(licId)
      ? null
      : `This serial has already been used for an upgrade on lic:${licId}.`
    this.setState({serial, error}, () =>
      this.props.onChange(null, {name: 'max6_serial', value: getId(serial)})
    )
  }

  render = () => <SerialSearch
    disabled={this.props.disabled}
    zIndex={50}
    error={this.state.error}
    labelTxt='Max 2-6 serial number'
    popupTxt='Please enter a valid and unused Max 2-6 serial number. (Do not enter a Max 7+ license id)'
    onChange={this.handleChange}
    licenseId={R.path(['attributes', 'max6_serial'], this.props)}
  />
}

Max6Serial.propTypes = {
  disabled: PropTypes.bool.isRequired,
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const getId = R.compose(
  R.defaultTo(null),
  R.prop('_id'),
  R.defaultTo({})
)

const ignoreIfNull = R.ifElse(
  isNilOrEmpty,
  R.T
)
const isLicUpgradable = ignoreIfNull(licenseIsUpgradable)

class Max7PlusLicenseId extends React.PureComponent {
  state = {
    license: null,
    error: null
  }

  handleLicenseChange = (_e, license) => {
    const error = isLicUpgradable(license)
      ? null
      : 'This license cannot be used for an upgrade purchase'
    this.setState({license, error}, () => {
      this.props.onChange(null, {name: 'max7plus_license', value: getId(license)})
    })
  }

  render = () => <LicenseSearch
    disabled={this.props.disabled}
    error={this.state.error}
    labelTxt='Max 7+ license id'
    popupTxt='Please enter a valid and unused Max 7+ license id. (Do not enter a Max 2-6 serial number)'
    onChange={this.handleLicenseChange}
    licenseId={R.path(['attributes', 'max7plus_license'], this.props)}
  />
}

Max7PlusLicenseId.propTypes = {
  disabled: PropTypes.bool.isRequired,
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const StudentOrTeacher = ({onChange, attributes}) => (
  <BaseSelectField width={10}
    name='student_or_teacher'
    value={R.prop('student_or_teacher', attributes)}
    options={[{text: 'Student', value: 'Student'}, {text: 'Teacher', value: 'Teacher'}]}
    onChange={onChange}
    labelTxt='Student or Teacher'
    popupTxt='Please select if this user is a student or teacher.'
  />
)
StudentOrTeacher.propTypes = {
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const SchoolName = ({onChange, attributes}) => (
  <BaseInputField width={10}
    name='school_name'
    value={R.prop('school_name', attributes)}
    onChange={onChange}
    labelTxt='School Name'
    popupTxt='Please enter the school name.'
  />
)
SchoolName.propTypes = {
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const getIdImageFromItem = R.compose(
  R.trim,
  R.defaultTo(''),
  R.path(['attributes', 'id_image'])
)

const IdImageComponent = ({idImageUrl, idImageMime}) => notNilOrEmpty(idImageUrl)
  ? notNilOrEmpty(idImageMime) && /pdf/i.test(idImageMime)
    ? (<a href={idImageUrl} target='_blank' rel="noopener noreferrer" style={{float: 'right'}}>idImage.PDF</a>)
    : (<Image src={idImageUrl} size="small" />)
  : null;

IdImageComponent.propTypes = {
  idImageUrl: PropTypes.string.isRequired,
  idImageMime: PropTypes.string
};

class IdImage extends React.PureComponent {
  state = {
    uploadingIdImage: false,
    error: null,
    idImage: getIdImageFromItem(this.props),
    idImageUrl: null,
    idImageMime: null
  }

  componentDidMount () {
    if (this.state.idImageUrl) return
    if (isNilOrEmpty(this.state.idImage)) {
      return
    }
    this.getIdImageUrl()
  }

  getIdImageUrl = _ => {
    if (isNilOrEmpty(this.state.idImage)) return
    const url = `/id_image/${this.state.idImage}`
    this.setState({uploadingIdImage: true})
    return get(url, {})
      .then(body => {
        this.setState({
          uploadingIdImage: false,
          idImageUrl: R.prop('sig', body),
          idImageMime: R.prop('content-type', body)
        })
      })
      .catch(e => {
        console.log(e)
        this.setState({
          uploadingIdImage: false,
          idImageUrl: null,
          idImageMime: null
        })
      })
  }

  uploadImage = data => {
    this.setState({uploadingIdImage: true})
    post('/id_image', {data, user_id: this.state.userId})
      .then(body => {
        this.setState({
          idImage: R.prop('_id', body),
          uploadingIdImage: false,
          error: notNilOrEmpty(R.prop('_id', body)) ? null : 'Unable to upload the image.'
        }, () => this.getIdImageUrl()
          .then(_ => this.props.onChange(null, {data: {name: 'id_image', value: R.prop('_id', body)}}))
        )
      })
      .catch(e => this.setState({
        uploadingIdImage: false,
        error: R.path(['response', 'body', 'error'], e) || e.message || 'Could not upload image'
      }))
  }

  onFileChange = (file) => {
    if (!file) {
      return window.alert('No file')
    }
    if (file.size && file.size > 5 * 1024 * 1024) {
      return window.alert('File size is too large. Please upload an image less than 3MB.')
    }
    try {
      const reader = new window.FileReader()
      reader.onloadend = (evt) => {
        if (!(evt && evt.target && evt.target.result)) {
          return
        }
        const data = evt.target.result
        this.uploadImage(data)
      }
      reader.readAsDataURL(file)
    } catch (e) {
      window.alert("Sorry, it seems your browser doesn't have the capabilities we need for image upload. Please try with a recent Firefox or Chrome browser.")
    }
  }

  onAction = (_actionType, {files}) => {
    if (files && files.length) {
      this.onFileChange(files[0])
    }
  }

  render = () => (
    <Dimmer.Dimmable as={Form.Group} dimmed={this.state.uploadingIdImage === true}>
      <Dimmer inverted active={this.state.uploadingIdImage === true} >
        <Loader />
      </Dimmer>
      <Form.Field>
        <FileItem
          actionType = {'upload'}
          accept={ UPLOAD_ACCEPT }
          onAction={this.onAction}
          label='Student/Teacher Id'
          tooltip='Please upload the student/teacher identification.'
        />
      </Form.Field>
      <Form.Field>
        <IdImageComponent {...this.state} />
      </Form.Field>
    </Dimmer.Dimmable>
  )
}

IdImage.propTypes = {
  userId: PropTypes.string,
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const AcademicItems = ({onChange, userId, attributes}) => (
  <React.Fragment>
    <IdImage onChange={onChange} attributes={attributes} userId={userId} />
    <StudentOrTeacher onChange={onChange} attributes={attributes} />
    <SchoolName onChange={onChange} attributes={attributes} />
  </React.Fragment>
)
AcademicItems.propTypes = {
  userId: PropTypes.string,
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const UpgradeItems = ({onChange, attributes}) => (
  <React.Fragment>
    <Max7PlusLicenseId
      disabled={notNilOrEmpty(R.prop('max6_serial', attributes))}
      onChange={onChange}
      attributes={attributes}
    />
    <Max6Serial
      disabled={notNilOrEmpty(R.prop('max7plus_license', attributes))}
      onChange={onChange}
      attributes={attributes}
    />
  </React.Fragment>
)

UpgradeItems.propTypes = {
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const ItemAttributes = ({product, attributes, userId, onChange}) => {
  switch (product) {
    case 'max-8-crossgrade':
      return (<Max4LiveSerial onChange={onChange} attributes={attributes} />)
    case 'max-annual-subscription-academic':
    case 'max-8-academic':
      return (<AcademicItems onChange={onChange} attributes={attributes} />)
    case 'max-8-academic-upgrade':
      return (
        <React.Fragment>
          <AcademicItems onChange={onChange} attributes={attributes} userId={userId} />
          <UpgradeItems onChange={onChange} attributes={attributes} />
        </React.Fragment>
      )
    case 'max-8-upgrade':
      return (<UpgradeItems onChange={onChange} attributes={attributes} />)
    default:
      return null
  }
}

ItemAttributes.propTypes = {
  product: PropTypes.string.isRequired,
  userId: PropTypes.string,
  attributes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

const mapItem = props => ({
  product: R.prop('product', props),
  display: R.prop('display', props),
  attributes: R.pickBy(notNilOrEmpty, {
    max7plus_license: R.path(['attributes', 'max7plus_license'], props),
    max6_serial: R.path(['attributes', 'max6_serial'], props),
    id_image: R.path(['attributes', 'id_image'], props),
    student_or_teacher: R.path(['attributes', 'student_or_teacher'], props),
    school_name: R.path(['attributes', 'school_name'], props),
    max4live_serial: R.path(['attributes', 'max4live_serial'], props)
  })
})

class Item extends React.PureComponent {
  onChange = (_e, data) => this.props.onChange(this.props.idx, {
    product: this.props.product,
    display: this.props.display,
    attributes: R.merge(this.props.attributes, {[data.name]: data.value})
  })

  render = () => (
    <Segment>
      <LineItem label='Product' text={R.prop('display', this.props)} />
      <Divider />
      <ItemAttributes {...R.pick(['attributes', 'userId', 'product'], this.props)} onChange={this.onChange} />
    </Segment>
  )
}

Item.propTypes = {
  idx: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  product: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  userId: PropTypes.string,
  attributes: PropTypes.object.isRequired
}

const mapItems = R.compose(
  R.map(mapItem),
  R.defaultTo([])
)

const mapItemsFromProps = R.compose(
  mapItems,
  R.path(['order', 'items'])
)

const getTagsFromOrder = user => R.compose(
  R.merge(R.__, R.pickBy(notNilOrEmpty, {user_id: getId(user)})),
  R.path(['order', 'tags'])
)

const getUserError = state => R.path(['user', '_id'], state)
  ? 'This will update the Fastspring record and will auto-attach the license to this user when you resend order notifications. This is only a warning.'
  : null

const hasNotNull = f => R.compose(
  notNilOrEmpty,
  R.path(['attributes', f])
)

const hasIdImage = R.compose(
  notNilOrEmpty,
  getIdImageFromItem
)
const hasStudentOrTeacher = hasNotNull('student_or_teacher')
const hasSchoolName = hasNotNull('school_name')
const hasAcademicFields = R.allPass([hasIdImage, hasSchoolName, hasStudentOrTeacher])
const validateAcademic = item => new Promise((resolve, reject) => hasAcademicFields(item)
  ? resolve()
  : reject(new Error('Missing academic fields'))
)

const validateUpgrade = item => new Promise((resolve, reject) => {
  const max6Serial = R.path(['attributes', 'max6_serial'], item)
  const max7Plus = R.path(['attributes', 'max7plus_license'], item)

  if (notNilOrEmpty(max6Serial) && notNilOrEmpty(max7Plus)) {
    return reject(new Error('You cannot have both a Max 7+ license id and a Max 2-6 SerialNumber for upgrade purchase.'))
  }
  if (isNilOrEmpty(max6Serial) && isNilOrEmpty(max7Plus)) {
    return reject(new Error('You must have either a Max 7+ license id or a Max 2-6 SerialNumber for upgrade purchase.'))
  }
  if (notNilOrEmpty(max6Serial)) {
    const url = `/serials/${max6Serial}`
    return get(url, {})
      .then(R.path(['license', '_id']))
      .then(licId => licId
        ? reject(new Error('The max 2-6 serial has already been used.'))
        : resolve()
      )
  }
  if (notNilOrEmpty(max7Plus)) {
    const url = `/licenses/${max7Plus}`
    return get(url, {})
      .then(lic => isLicUpgradable(lic)
        ? resolve()
        : reject(new Error('This license cannot be used for an upgrade purchase.'))
      )
  }
  return reject(new Error('This license does not seem to be valid.'))
})

const validateAcademicUpgrade = item => Promise.all([validateUpgrade(item), validateAcademic(item)])

const validateCrossgrade = item => get(`/fastspring/max4liveserial?value=${R.path(['attributes', 'max4live_serial'], item)}`, {})
  .then(_ => Promise.resolve())

const noEditingNecessary = item => Promise.reject(new Error(`No editing is necessary on a ${R.prop('product', item)} order.`))
const unRecognizedItem = item => Promise.reject(new Error(`This item (${R.prop('product', item)}) is not recognized and editing won't be necessary.`))

const validateItem = R.cond([
  [R.propEq('product', 'max-8-academic-upgrade'), validateAcademicUpgrade],
  [R.propEq('product', 'max-8-academic'), validateAcademic],
  [R.propEq('product', 'max-8-upgrade'), validateUpgrade],
  [R.propEq('product', 'max-8-crossgrade'), validateCrossgrade],
  [R.propEq('product', 'max-annual-subscription-academic'), validateAcademic],

  [R.propEq('product', 'max-8'), noEditingNecessary],
  [R.propEq('product', 'max-monthly-subscription'), noEditingNecessary],
  [R.propEq('product', 'max-annual-subscription'), noEditingNecessary],

  [R.T, unRecognizedItem]
])

const filterItems = R.compose(
  R.filter(notNilOrEmpty),
  R.flatten
)

const validateOrder = order => Promise.all(order.items.map(validateItem))
  .then(filterItems)
  .then(a => a.length
    ? window.alert(a[0])
    : order
  )

const hasAttr = R.compose(
  notNilOrEmpty,
  R.keys,
  R.defaultTo({}),
  R.path(['order', 'items', 0, 'attributes'])
)

const hasUserId = R.compose(
  notNilOrEmpty,
  R.path(['order', 'tags', 'user_id'])
)

class Order extends React.PureComponent {
  state = {
    dirty: false,
    user: null,
    items: mapItemsFromProps(this.props)
  }

  handleUserChange = (_e, user) => {
    this.setState({user})
  }

  onChange = (idx, data) => this.setState({
    dirty: true,
    items: R.update(idx, data, this.state.items)
  })

  onCancel = _ => this.setState({
    dirty: false,
    user: null,
    items: mapItemsFromProps(this.props)
  })

  onSubmit = _e => !this.state.dirty
    ? null
    : validateOrder(this.state)
      // .catch(e => console.log('got error, but will continue anyway', e))
      .then(_ => R.pickBy(notNilOrEmpty, {
        order: R.prop('order', this.props.order),
        tags: getTagsFromOrder(this.state.user)(this.props.order),
        items: mapItems(this.state.items)
      }))
      .then(data => hasUserId(this.props) || hasAttr(this.props)
        ? window.confirm(`Order ${R.prop('order', this.props.order)} already has a user and/or attributes. You shouldn't really update it as this feature is only meant for custom orders that have no user input fields. Are you sure you want to continue?`)
          ? patch(`/fastspring/orders/${data.order}`, data)
          : null
        : patch(`/fastspring/orders/${data.order}`, data)
      )
      .then(x => console.log('after validation and patching', x))
      .then(_ => this.setState({dirty: false}))
      .catch(window.alert)

  render = () => {
    const order = this.props.order
    return (
      <Segment>
        <Form onSubmit={this.onSubmit} >
          <Grid columns={2}>
            <Grid.Row>
              <Grid.Column width={5}>
                <LineItem label='Order Id' text={R.prop('id', order)} />
                <LineItem label='Reference' text={R.prop('reference', order)} />
                <Button
                  as='a'
                  content='view in Fastspring'
                  size='mini'
                  href={getFsUrl('order')(R.prop('id', order))}
                  target='_blank'
                />
              </Grid.Column>
              <Grid.Column width={5}>
                <LineItem label='First Name' text={R.path(['customer', 'first'], order)} />
                <LineItem label='Last Name' text={R.path(['customer', 'last'], order)} />
                <LineItem label='Email' text={R.path(['customer', 'email'], order)} />
                <LineItem label='User Id' text={R.path(['tags', 'user_id'], order)} />
              </Grid.Column>
              <Grid.Column width={6}>
                {
                  R.path(['tags', 'user_id'], order)
                    ? null
                    : <UserSearch
                      onChange={this.handleUserChange}
                      labelTxt="Cycling '74 User Account"
                      popupTxt="Set the user for this fastspring purchase"
                      error={getUserError(this.state)}
                    />
                }
              </Grid.Column>
            </Grid.Row>
          </Grid>
          { this.state.items.map((item, idx) => <Item
            {...item}
            idx={idx}
            onChange={this.onChange}
            userId={R.path(['tags', 'user_id'], order)}
            key={`item_${idx}`}
          />)
          }
          <Button.Group>
            <Button disabled={!this.state.dirty} onClick={this.onCancel}>Cancel</Button>
            <Button.Or />
            <Button positive={this.state.dirty} disabled={!this.state.dirty} type="submit">Save</Button>
          </Button.Group>
        </Form>
      </Segment>
    )
  }
}

Order.propTypes = {
  order: PropTypes.object.isRequired
}

export default Order



// WEBPACK FOOTER //
// ./webAdmin/components/fastspring/order.js